import styled from "@emotion/styled"
import Dialog from "@mui/material/Dialog"

const StyledSystemSetting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  padding: 20px 22px 24px;
  border-radius: 24px;
  background: radial-gradient(
      circle at 4% -8%,
      rgba(99, 102, 241, 0.14) 0%,
      transparent 35%
    ),
    radial-gradient(
      circle at 98% 108%,
      rgba(14, 165, 233, 0.12) 0%,
      transparent 42%
    ),
    var(--cms-content-bg);

  .heading-copy {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .page-title {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 0;
    color: var(--cms-text-secondary);
    font-size: 13px;
    font-weight: 500;
  }

  .page-surface {
    border: 1px solid var(--cms-border);
    border-radius: 18px;
    background: var(--cms-surface-secondary);
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
    padding: 14px;
  }

  .page-empty {
    border: 1px dashed var(--cms-border);
    border-radius: 12px;
    background: var(--cms-surface-muted);
    padding: 26px 16px;
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
  }

  .color-box {
    width: 88px;
    height: 35px;
    line-height: 35px;
    font-size: 12px;
    text-transform: uppercase;
    display: inline-block;
    text-align: center;
    margin: 10px 0;
    color: black;
    border-radius: 3px;
    &.dark-mode {
      color: #f7eeee;
    }
    box-shadow: rgba(50, 50, 93, 0.25) 0px 2px 5px -1px,
      rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
  }
`

const StyledSystemSettingDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(880px, calc(100vw - 28px));
    max-width: none;
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 18px 22px 8px;
  }

  .MuiDialogContent-root {
    display: flex;
    width: auto;
    flex-direction: column;
    max-height: min(76vh, 860px);
    min-width: min(760px, calc(100vw - 28px));
    padding: 10px 22px 10px;
    overflow: auto;
  }

  .MuiDialogActions-root {
    padding: 8px 22px 18px;
    gap: 10px;
  }

  .dialog-title-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .dialog-subtitle {
    margin: 0;
    font-size: 13px;
    color: var(--cms-text-secondary);
    font-weight: 500;
  }

  .dialog-cancel-btn,
  .dialog-submit-btn {
    text-transform: none;
    border-radius: 10px;
    min-height: 40px;
    font-weight: 700;
    padding: 8px 14px;
  }

  .dialog-submit-btn {
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.24);
  }

  .MuiFormControl-root {
    margin: 0 0 12px;
    width: 100%;
  }

  .dialog-label {
    margin: 2px 0 8px;
    font-size: 12px;
    color: var(--cms-text-secondary);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .ql-editor {
    height: 150px;
    overflow: auto;
  }

  .image-select-frame {
    display: flex;
    min-height: 140px;
    width: min(100%, 340px);
    border: 2px dashed var(--cms-border);
    font-size: 14px;
    color: var(--cms-text-secondary);
    align-items: center;
    justify-content: space-around;
    margin-left: 0;
    cursor: pointer;
    text-transform: capitalize;
    padding: 10px;
    border-radius: 9px;
    background: var(--cms-surface-muted);
  }

  .resource-section {
    border: 1px solid var(--cms-border);
    border-radius: 10px;
    background: var(--cms-surface-muted);
    padding: 10px;
  }

  .color-box {
    width: 100px;
    height: 60px;
    font-size: 15px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 10px 0;
    color: black;
    border-radius: 3px;
    &.dark-mode {
      color: #f7eeee;
    }
    box-shadow: rgba(50, 50, 93, 0.25) 0px 2px 5px -1px,
      rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;
  }
`

const StyledInputLabel = styled.h4`
  margin: 2px 0 8px;
  font-size: 12px;
  color: var(--cms-text-secondary);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const StyledSettingTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .MuiTableContainer-root {
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
  }

  .MuiTableCell-head {
    background: var(--cms-table-head-bg);
    color: var(--cms-text-primary);
    font-weight: 700;
    font-size: 12px;
    border-bottom: 1px solid var(--cms-border);
  }

  .MuiTableCell-body {
    font-size: 13px;
    color: var(--cms-text-primary);
    border-bottom: 1px solid var(--cms-border);
    vertical-align: top;
    overflow-wrap: anywhere;
  }

  .type-chip {
    font-size: 11px;
    height: 22px;
    font-weight: 700;
    margin-right: 8px;
  }

  .type-chip.text {
    background: #dbeafe;
    color: #1d4ed8 !important;
  }

  .type-chip.number {
    background: #ede9fe;
    color: #6d28d9 !important;
  }

  .type-chip.link {
    background: #dcfce7;
    color: #166534 !important;
  }

  .type-chip.html {
    background: #fef3c7;
    color: #92400e !important;
  }

  .type-chip.media {
    background: #e2e8f0;
    color: var(--cms-text-primary) !important;
  }

  .type-chip.color {
    background: #e0e7ff;
    color: #3730a3 !important;
  }

  .content-line {
    margin-top: 7px;
    color: var(--cms-text-primary);
    line-height: 1.45;
    word-break: break-word;
  }

  .modified-date {
    color: var(--cms-text-secondary);
    font-weight: 600;
    font-size: 12px;
  }

  .row-actions {
    display: flex;
    justify-content: flex-end;
  }

  .row-actions .MuiButton-root {
    text-transform: none;
    border-radius: 9px;
    min-height: 34px;
    font-weight: 700;
    padding: 4px 10px;
  }

  .table-empty {
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
    padding: 20px 12px;
  }

  [data-cms-theme="dark"] & {
    .type-chip.text {
      background: rgba(37, 99, 235, 0.24);
      color: #93c5fd !important;
    }

    .type-chip.number,
    .type-chip.color {
      background: rgba(124, 58, 237, 0.24);
      color: #c4b5fd !important;
    }

    .type-chip.link {
      background: rgba(22, 163, 74, 0.24);
      color: #86efac !important;
    }

    .type-chip.html {
      background: rgba(217, 119, 6, 0.24);
      color: #fde68a !important;
    }
  }
`

export {
  StyledSystemSetting,
  StyledSystemSettingDialog,
  StyledInputLabel,
  StyledSettingTable,
}
